import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const fade = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

export default function About() {
  return (
    <main
      data-testid="about-page"
      className="relative bg-[#0A0A0C] text-[#F5F5F5] min-h-screen overflow-x-hidden"
    >
      <Nav />

      <section
        data-testid="about-bio"
        className="relative bg-black overflow-hidden"
      >
        {/* Ambient blue glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[600px] opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(220,38,38,0.15) 0%, rgba(127,29,29,0.04) 40%, transparent 70%)",
          }}
        />
        {/* Faint grid texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 80%)",
          }}
        />

        {/* Vertical edition labels */}
        <div
          aria-hidden
          className="hidden xl:flex absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-center font-mono text-[10px] uppercase tracking-[0.45em] text-white/25 select-none"
        >
          Cameron Bergsma · Biography · MMXXVI
        </div>
        <div
          aria-hidden
          className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 rotate-90 origin-center font-mono text-[10px] uppercase tracking-[0.45em] text-white/25 select-none"
        >
          Long Read · 01 of 01
        </div>

        <motion.div
          className="relative pt-24 md:pt-32"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          {/* Establishing shot — Stratford City Hall (wide cinematic) */}
          <motion.figure
            className="relative mx-auto max-w-[1280px] px-6 md:px-10"
            variants={fade}
            data-testid="about-stratford-img"
          >
            <div className="relative rounded-2xl md:rounded-[1.75rem] overflow-hidden border border-white/10 shadow-2xl aspect-[16/9] md:aspect-[21/9]">
              <img
                src="https://customer-assets.emergentagent.com/job_cameron-portfolio/artifacts/ezfeqc7s_Screenshot%202026-05-12%20at%202.39.19%E2%80%AFPM.png"
                alt="Stratford, Ontario — City Hall"
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover object-center select-none"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)",
                }}
              />
              <figcaption className="absolute left-5 bottom-5 md:left-7 md:bottom-7 flex items-center gap-3 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/15">
                <span className="h-1.5 w-1.5 rounded-full bg-[#DC2626] shadow-[0_0_8px_rgba(220,38,38,0.9)]" />
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.32em] text-white/85">
                  Stratford · Ontario · Where it began
                </span>
              </figcaption>
            </div>
          </motion.figure>

          {/* Narrow content column */}
          <div className="mx-auto max-w-[780px] px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32">
            {/* Title block */}
            <motion.div variants={fade} className="text-center">
              <div className="flex items-center justify-center gap-3 mb-8">
                <span className="h-px w-10 bg-white/30" />
                <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55">
                  Biography · 2026
                </span>
                <span className="h-px w-10 bg-white/30" />
              </div>

              <h1
                className="font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl text-white"
                data-testid="about-name"
              >
                Cameron
                <br />
                <span
                  className="font-script italic font-normal"
                  style={{
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 70%, #7F1D1D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                    paddingBottom: "0.18em",
                    paddingRight: "0.05em",
                    display: "inline-block",
                    lineHeight: 1,
                  }}
                >
                  Bergsma.
                </span>
              </h1>

              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-white/45">
                Builder · Investor · Author
              </p>
            </motion.div>

            {/* Divider flourish */}
            <motion.div
              className="my-12 md:my-16 flex items-center justify-center gap-4"
              variants={fade}
              aria-hidden
            >
              <span className="h-px w-16 bg-white/15" />
              <span
                className="font-script italic text-2xl select-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(147,197,253,0.7) 0%, rgba(220,38,38,0.3) 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                §
              </span>
              <span className="h-px w-16 bg-white/15" />
            </motion.div>

            {/* The bio */}
            <motion.article
              className="text-base md:text-lg text-white/80 leading-[1.85]"
              variants={fade}
              data-testid="about-bio-prose"
            >
              <p>
                <span className="float-left mr-3 mt-1 font-display font-black text-7xl md:text-8xl leading-[0.82] text-white">
                  C
                </span>
                ameron grew up in Stratford, Ontario — a small theatre town
                where the buildings feel older than the country itself. His
                early years were not always easy, and much of his childhood
                was shaped by quiet struggles with mental health and learning
                how to navigate life while carrying challenges he did not
                always know how to explain. But those experiences would later
                become the foundation for the resilience, empathy, and
                perspective that now define the way he approaches both
                business and life.
              </p>

              <div className="my-10 flex items-center justify-center gap-2 opacity-50" aria-hidden>
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
              </div>

              <p>
                While studying Global Business & Digital Arts at the
                University of Waterloo, Cameron worked four jobs at once to
                support himself through school. Long nights, uncertainty,
                and relentless pressure became part of daily life, but so
                did the belief that difficult circumstances did not have to
                define a person's future. Around that time, he experienced
                two moments he still remembers vividly: finding a penny
                with a cross burnt into its face, and years later, another
                penny lasered straight through end to end on a completely
                different street. He had been praying frequently during
                those periods of his life, and while he does not claim to
                fully understand the meaning behind those moments, they
                stayed with him as reminders that even in uncertainty,
                there is always a reason to keep moving forward.
              </p>

              <div className="my-10 flex items-center justify-center gap-2 opacity-50" aria-hidden>
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
              </div>

              <p>
                Before finding success, Cameron experienced failure
                repeatedly. Eight ventures collapsed before one finally
                gained traction. Each setback taught him something new
                about leadership, resilience, and the importance of
                continuing even when progress feels invisible. When he was
                eventually let go from his retail job at The Brick, what
                initially felt like a devastating moment unexpectedly
                became the catalyst for an entirely new chapter. That
                experience would later inspire his book,{" "}
                <em className="text-white not-italic font-medium">
                  The Butterfly Effect
                </em>
                , centered on the idea that the smallest moments in life
                can quietly create the biggest transformations.
              </p>

              <div className="my-10 flex items-center justify-center gap-2 opacity-50" aria-hidden>
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
                <span className="h-1 w-1 rounded-full bg-[#DC2626]" />
              </div>

              <p>
                Today, Cameron builds companies, leads GoBeyond Ventures,
                and continues exploring the intersection of technology,
                creativity, and human potential. Beyond entrepreneurship,
                he remains deeply passionate about storytelling,
                innovation, and helping others believe that difficult
                beginnings do not disqualify someone from building an
                extraordinary future. In his spare time, he streams anime
                and videogames late at night, keeps a close circle of
                honest friends around him, and continues living by a
                simple philosophy: never call something impossible until
                you have truly tried.
              </p>
            </motion.article>

            {/* Closing signature */}
            <motion.div
              className="mt-16 md:mt-20 flex flex-col items-center gap-4"
              variants={fade}
            >
              <span className="h-px w-16 bg-white/20" />
              <span
                className="font-script italic text-3xl md:text-4xl"
                style={{
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #FCA5A5 50%, #DC2626 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  lineHeight: 1.1,
                }}
              >
                Cameron Bergsma
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
                Toronto · Canada
              </span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </main>
  );
}
